import PropTypes from "prop-types";
import { useCallback, useMemo } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { TableLink } from "components/BaseTableV3/TableLink";
import { getNextQueryParams } from "components/helpers";
import RuvixxDate from "components/RuvixxDate";
import { SELECT_COLUMN_ID } from "constants/Constants";
import usePageTitle from "hooks/usePageTitle";
import TableIconBadge from "routes/custom_tables/TableIconBadge";
import {
  EVENT_FILTER,
  omitScopedFilters,
  SESSION_FILTER,
} from "routes/swoogo/filterOptions";
import { linkCell } from "routes/swoogo/linkCell";
import { swoogoAttendanceKeys } from "routes/swoogo/queryKeys";
import useSwoogoConnected from "routes/swoogo/useSwoogoConnected";
import SwoogoService from "services/Swoogo";

const HEADER = "Swoogo Attendances";

const renderDate = ({ row, column }) => {
  const date = row.getValue(column.id);
  return date ? <RuvixxDate date={date} /> : null;
};

const COLUMNS = [
  {
    id: SELECT_COLUMN_ID,
    size: 60,
    enableSorting: false,
    meta: { isDisplay: true },
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onCheckedChange={checked => table.toggleAllRowsSelected(!!checked)}
        aria-label="Select all rows"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        indeterminate={row.getIsSomeSelected() && !row.getIsSelected()}
        onCheckedChange={checked => row.toggleSelected(!!checked)}
        aria-label="Select row"
      />
    ),
  },
  { id: "ID", accessorKey: "id", header: "ID", size: 90 },
  {
    id: "Registrant",
    accessorKey: "registrant_name",
    sortKey: "registrant_id",
    header: "Registrant",
    size: 200,
    cell: linkCell(
      ({ registrant_id }) => `/swoogo/registrants/${registrant_id}`
    ),
  },
  {
    id: "Email",
    accessorKey: "registrant_email",
    header: "Email",
    size: 260,
    enableSorting: false,
  },
  {
    id: "Session",
    accessorKey: "session_name",
    sortKey: "session_id",
    header: "Session",
    size: 240,
    cell: linkCell(({ session_id }) => `/swoogo/sessions/${session_id}`),
  },
  {
    id: "Check In",
    accessorKey: "check_in_date",
    header: "Check In",
    size: 160,
    cell: renderDate,
  },
  {
    id: "Check Out",
    accessorKey: "check_out_date",
    header: "Check Out",
    size: 160,
    cell: renderDate,
  },
  {
    id: "Duration (min)",
    accessorKey: "duration",
    header: "Duration (min)",
    size: 130,
  },
  {
    id: "Contact",
    accessorKey: "contact_name",
    header: "Contact",
    size: 200,
    enableSorting: false,
    cell: ({ row }) =>
      row.original.contact_id ? (
        <TableLink href={`/contacts/${row.original.contact_id}`}>
          {row.original.contact_name}
        </TableLink>
      ) : (
        <span className="tw:text-[var(--color-text-muted)]">Not linked</span>
      ),
  },
];

const FILTERS = [EVENT_FILTER, SESSION_FILTER];

const AttendancesTable = ({ scope }) => {
  usePageTitle(scope ? null : HEADER);

  const { lastSyncedAt } = useSwoogoConnected();
  const queryKey = useMemo(
    () => swoogoAttendanceKeys.list(lastSyncedAt),
    [lastSyncedAt]
  );
  const filters = useMemo(() => omitScopedFilters(FILTERS, scope), [scope]);

  const fetchAttendances = useCallback(
    async queryParams =>
      SwoogoService.listAttendances(getNextQueryParams(queryParams, COLUMNS)),
    []
  );

  return (
    <BaseTableV3
      columns={COLUMNS}
      headerIcon={<TableIconBadge iconName="clipboard-check" />}
      header={HEADER}
      tableName="swoogo_session_attendances"
      queryKey={queryKey}
      fetchData={fetchAttendances}
      filters={filters}
      extraQueryParams={scope}
      enableSearch
      enableSettings
    />
  );
};

AttendancesTable.propTypes = {
  scope: PropTypes.objectOf(PropTypes.number),
};

export default AttendancesTable;
